/**
 * AnalyticsTracker
 *
 * Rendered once inside <Router> in App.js.  Initialises GA4 on mount and
 * fires a page_view event on every route change — but only when the user is
 * on the Start portal (/start/*).  CRM and client-portal paths are ignored
 * intentionally (out of scope for V1 Lead Portal analytics).
 *
 * Also syncs the Firebase UID + accountId to GA4 whenever the auth state
 * changes so that per-user engagement can be attributed in the report page.
 *
 * The same route change is mirrored into the lead's own Start activity log
 * (users/{uid}/startActivity) so the CRM Lead overview → Notes timeline can
 * show which pages they actually opened. Internal users browsing /start are
 * skipped — their clicks aren't lead behaviour.
 *
 * Client-portal (/portal/*) route changes are likewise mirrored into
 * users/{uid}/portalActivity so the CRM Client overview → Access tab can show
 * per-member portal activity. Same internal-user guard; GA4 stays Start-only.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  initGA4,
  trackPageView,
  setAnalyticsUser,
  clearAnalyticsUser,
} from '../services/analyticsService';
import { logStartPageView } from '../services/startActivityLog';
import { logPortalPageView } from '../services/portalActivityLog';

const AnalyticsTracker = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const lastPathRef = useRef(null);
  const lastUidRef = useRef(null);
  // null = not resolved yet, so we hold page-view logging until we know
  const [isInternalUser, setIsInternalUser] = useState(null);
  const pendingStartPathsRef = useRef([]);
  const pendingPortalPathsRef = useRef([]);
  const lastPortalPathRef = useRef(null);

  // Initialise GA4 once on mount
  useEffect(() => {
    initGA4();
  }, []);

  // Resolve once per signed-in user whether they're a CRM/internal user
  useEffect(() => {
    if (!currentUser) {
      setIsInternalUser(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (!cancelled) setIsInternalUser(snap.exists());
      } catch {
        // Can't tell — don't log rather than pollute a lead's timeline
        if (!cancelled) setIsInternalUser(true);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Track page views on route changes (Start portal only)
  useEffect(() => {
    const { pathname } = location;
    if (!pathname.startsWith('/start')) return;
    if (pathname === lastPathRef.current) return;
    lastPathRef.current = pathname;
    trackPageView(pathname);
    // Queued until the internal_users check resolves; flushed by the effect below
    pendingStartPathsRef.current.push(pathname);
  }, [location]);

  // Mirror the visits into the lead's own activity log once we know who they are
  useEffect(() => {
    if (!pendingStartPathsRef.current.length) return;
    // Internal users (and signed-out visitors) never contribute to a lead's timeline
    if (isInternalUser === true || !currentUser) {
      pendingStartPathsRef.current = [];
      return;
    }
    if (isInternalUser !== false) return;
    const queued = pendingStartPathsRef.current;
    pendingStartPathsRef.current = [];
    queued.forEach((pathname) => logStartPageView(currentUser, pathname));
  }, [location, currentUser, isInternalUser]);

  // Queue client-portal page views (no GA4 — Firestore mirror only)
  useEffect(() => {
    const { pathname } = location;
    if (!pathname.startsWith('/portal')) return;
    if (pathname === lastPortalPathRef.current) return;
    lastPortalPathRef.current = pathname;
    // Queued until the internal_users check resolves; flushed by the effect below
    pendingPortalPathsRef.current.push(pathname);
  }, [location]);

  // Flush them into the portal user's own activity log once we know who they are
  useEffect(() => {
    if (!pendingPortalPathsRef.current.length) return;
    // Internal users (e.g. CRM Portal View) and signed-out visitors are not client behaviour
    if (isInternalUser === true || !currentUser) {
      pendingPortalPathsRef.current = [];
      return;
    }
    if (isInternalUser !== false) return;
    const queued = pendingPortalPathsRef.current;
    pendingPortalPathsRef.current = [];
    queued.forEach((pathname) => logPortalPageView(currentUser, pathname));
  }, [location, currentUser, isInternalUser]);

  // Sync user identity when auth state changes
  useEffect(() => {
    if (!currentUser) {
      if (lastUidRef.current) {
        clearAnalyticsUser();
        lastUidRef.current = null;
      }
      return;
    }

    if (currentUser.uid === lastUidRef.current) return;
    lastUidRef.current = currentUser.uid;

    // Fetch accountId from the users collection so we can pass it as a user property
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const accountId = userDoc.exists() ? userDoc.data().accountId : undefined;
        setAnalyticsUser(currentUser.uid, accountId);
      } catch {
        // Non-fatal — set user without accountId
        setAnalyticsUser(currentUser.uid, undefined);
      }
    })();
  }, [currentUser]);

  // This component renders nothing
  return null;
};

export default AnalyticsTracker;
